import {
  applyInstanceIds,
  archiveDashboard,
  createDashboard,
  duplicateDashboard,
  getDashboard,
  getTemplate,
  listDashboardCategories,
  listDashboards,
  listSectionFilters,
  listSections,
  listTemplates,
  listWidgetCategories,
  listWidgets,
  publishDashboard,
  updateDashboard,
} from '../../../services';
import { DASHBOARD_STATES } from '../constants';
import { SECTION_FALLBACK_FILTERS, SECTION_FALLBACK_TREE } from '../../../shared/constants/sectionFallback.js';
import defaultDashboardDataAdapters from './defaultDataAdapters';

/**
 * API-backed data adapters for the dashboards domain (CT-5183).
 *
 * Same interface `DashboardDataContext` already consumed, so the pages, the
 * editor and the modals are untouched: only where the data comes from changes.
 *
 * The module is live, so an empty list is the truth and is shown as such —
 * with one exception, the section catalog (`fetchSections` / `fetchSectionFilters`),
 * which falls back to `SECTION_FALLBACK_TREE` — documented on those adapters.
 */

/**
 * Draft revisions, by dashboard uuid.
 *
 * `update` must echo the revision the last read or write returned, or the
 * backend answers `DSW021`. The editor has no field for it and the context's
 * adapter signature has no room for one, so the token is remembered here —
 * the one place that both reads and writes pass through. A cache miss simply
 * omits `expectedRevision`, which is what a metadata-only save does anyway.
 */
const revisions = new Map();

const rememberRevision = (id, revision) => {
  if (id && revision != null) revisions.set(id, revision);
};

/**
 * The write endpoints return an operation result, not a dashboard, but the
 * context upserts whatever comes back into its list. Merging the payload that
 * was just sent with the ids the server assigned gives the card everything it
 * needs without a second round trip.
 */
const toUpsertedDashboard = (id, payload, result) => {
  const updatedAt = result?.updatedAt ?? new Date().toISOString();
  const hasComposition = Array.isArray(payload?.widgets);

  return {
    ...payload,
    id,
    // Only when a composition was actually sent. A metadata-only save — a
    // rename, a restore — carries no widgets, and writing a count from it
    // would blank the card's widget count until the next refetch.
    ...(hasComposition
      ? {
        // Adopt the `clientRef → instanceUuid` pairs the write returned.
        // Without this the editor keeps its own client ids, and the next save
        // sends the same placements as new ones again — one duplicate
        // instance per save.
        widgets: applyInstanceIds(payload.widgets, result?.instanceIds ?? []),
        widgetCount: payload.widgets.length,
      }
      : {}),
    // The list sorts non-archived cards by `createdAt`. The backend does not
    // return one, so a card without it ties at epoch 0 and a dashboard that
    // was just created or saved sinks to the bottom.
    createdAt: payload?.createdAt ?? updatedAt,
    updatedAt,
  };
};

/**
 * Every page of a list, not just the first.
 *
 * The screens render what these adapters return as the whole list — there is
 * no paging control on /dashboards — so stopping at page one would silently
 * hide everything past the twentieth row. The cap is there so a runaway
 * `hasNext` cannot spin forever; hitting it is worth a line in the console
 * because it means the screen genuinely needs pagination.
 */
const MAX_PAGES = 20;

/**
 * Every keyset page of one section.
 *
 * A page that fails after earlier pages already came back does not erase
 * them — those rows are real and already answered for, so only a first-page
 * failure (nothing gathered yet) is left to bubble up as "this section is
 * unknown", which is what the caller uses to distinguish `DSW024` from a
 * genuine mid-list error.
 */
const listAllInSection = async (sectionId) => {
  const all = [];
  let cursor;
  try {
    for (let page = 0; page < MAX_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop -- keyset paging is
      // sequential by definition: the next request needs the previous
      // response's cursor.
      const { dashboards, meta } = await listDashboards({ sectionId, cursor });
      all.push(...dashboards);
      const next = meta?.nextCursor;
      if (!next || next === cursor) break;
      cursor = next;
      if (page === MAX_PAGES - 1) {
        console.warn(
          `[dashboards] stopped section "${sectionId}" after ${MAX_PAGES} pages — the `
          + 'screen needs a paging control.',
        );
      }
    }
  } catch (error) {
    if (all.length) return all;
    throw error;
  }
  return all;
};

/**
 * TODO(TEMP-FALLBACK): client-side filtering — move `search` / `state` / category to
 * the server once `dashboard/list` accepts them (CT-5470), then drop this.
 *
 * `search`, `state` and the category, applied here because the keyset read
 * takes none of them — it rejects `searchKeyword`, `state` and `categoryKeys`
 * with `DSW001` rather than ignoring them. The list page still does its own
 * narrowing on top; this is only what used to be server-side.
 */
const matchingFilters = (rows, { search, state, categoryId, categoryKeys } = {}) => {
  const needle = (search ?? '').trim().toLowerCase();
  // The Created tab keeps a single-select category, the contract speaks the
  // plural — same pairing `listTemplates` has.
  const wanted = categoryKeys?.length ? categoryKeys : (categoryId ? [categoryId] : []);

  return rows.filter((row) => {
    if (state && row.state !== state) return false;
    if (wanted.length && !wanted.some((key) => (row.categoryKeys ?? []).includes(key))) {
      return false;
    }
    if (!needle) return true;
    return `${row.title ?? ''} ${row.description ?? ''}`.toLowerCase().includes(needle);
  });
};

const fetchAllPages = async (fetchPage, pick) => {
  const all = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    // Sequential on purpose: the next page is only worth asking for once this
    // one says there is one.
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchPage(page);
    all.push(...pick(response));
    if (!response.meta?.hasNext) return all;
  }
  console.warn(
    `[dashboards] stopped after ${MAX_PAGES} pages — the screen needs a paging control.`,
  );
  return all;
};


/**
 * A catalog widget → the card the builder's "Add widget" modal renders, and
 * the placement it becomes.
 *
 * The two shapes are the same object on purpose: the modal hands what the user
 * picked straight to `addWidgets`, and `toApiWidgetInstance` then reads
 * `widgetUuid` and `widgetVersionNumber` off it. Keeping `id === widgetUuid`
 * is what makes the instance point at the real catalog widget rather than at
 * something the builder invented.
 *
 * Presentation defaults are local because the widget contract has none: the
 * card offers three sizes and a standard class, which is what every seeded
 * widget offered before this was wired to the API.
 */
const DEFAULT_SIZES = Object.freeze(['small', 'medium', 'large']);

const toPlaceableWidget = (widget, categoryNames) => ({
  // The uuid, so the placement pins this catalog widget.
  id: widget.id,
  widgetUuid: widget.id,
  // `null` until the widget is published; the instance pins a version, and the
  // backend is the one that decides which.
  widgetVersionNumber: widget.publishedVersionNumber ?? null,
  name: widget.name,
  description: widget.description,
  type: widget.type,
  // The modal groups by this and prints it as a heading, so it wants the
  // category's name, not its key.
  category: categoryNames.get(widget.category) ?? widget.category ?? '',
  // No counterpart on a catalog row; the detail pane hides it when empty.
  bestFor: '',
  sizes: DEFAULT_SIZES,
  defaultSize: widget.size ?? 'medium',
  widgetClass: 'CLASS_2_STANDARD',
  defaultActions: [],
  // Carried so a surface can mark a widget that is not published yet.
  status: widget.status,
  hasDraft: widget.hasDraft,
});

const warnedCatalogFallback = new Set();

/**
 * One line per endpoint and reason, not one per session: an empty catalog and
 * a failing one must not hide each other, and a failure is an error, not a
 * notice — it can be an outage, an expired session or a mapper bug.
 *
 * Development only: in production the fallback is the whole answer — the
 * screen keeps working, the user has nothing to act on, and nobody reads the
 * console there.
 */
const warnCatalogFallback = (endpoint, error) => {
  if (!import.meta.env.DEV) return;
  const key = `${endpoint}:${error ? 'failed' : 'empty'}`;
  if (warnedCatalogFallback.has(key)) return;
  warnedCatalogFallback.add(key);
  const log = error ? console.error : console.warn;
  log(
    `[dashboards] ${endpoint} `
    + (error ? `failed (${error.message ?? 'unknown error'})` : 'returned no rows')
    + ' — serving the section fallback (TEMP-FALLBACK).',
  );
};

const apiDashboardDataAdapters = {
  /**
   * The Created tab — every section's dashboards, merged.
   *
   * `dashboard/list` is keyset and **scoped to one section**: `sectionKey` is
   * required and it rejects every filter and ordering input. The screen shows
   * one list across the workspace, so the sections are walked and the pages
   * merged, and `search` / `state` are applied here over the result. It costs
   * one request per section; there is no endpoint that answers in one.
   *
   * A section the backend does not know answers `DSW024`, which must not take
   * the whole list down with it — the catalogue the picker offers is still
   * partly local seed data, so unknown keys are expected. Those are skipped and
   * counted.
   */
  async fetchDashboards(filters = {}) {
    // The keys come from `section/list_filter` — the Section filter's own
    // catalog — not from `section/list`, which belongs to the Select-section
    // modal (CT-5468 B). Same TEMP-FALLBACK keys when it answers empty.
    const keys = filters.sectionId
      ? [filters.sectionId]
      : (await apiDashboardDataAdapters.fetchSectionFilters()).map((section) => section.id).filter(Boolean);

    const perSection = await Promise.all(keys.map(async (sectionId) => {
      try {
        return await listAllInSection(sectionId);
      } catch (error) {
        return { skipped: sectionId, error };
      }
    }));

    const rows = perSection.filter(Array.isArray).flat();
    const skipped = perSection.filter((entry) => !Array.isArray(entry));
    if (skipped.length) {
      // One line, not one per section: a workspace mid-migration will have
      // several, and a silent empty list is how you conclude the API is broken.
      console.warn(
        `[dashboards] ${skipped.length} of ${keys.length} sections could not be listed `
        + `(${skipped.map((s) => s.skipped).join(', ')}) — they are not in the backend's `
        + 'section catalogue.',
      );
    }
    return matchingFilters(rows, filters);
  },

  async fetchTemplates(filters = {}) {
    return fetchAllPages(
      (page) => listTemplates({ ...filters, page }),
      (response) => response.templates,
    );
  },

  /**
   * The sections a dashboard can be anchored to, from `section/list`.
   *
   * TEMP-FALLBACK: falls back to `SECTION_FALLBACK_TREE` when the catalog is empty or the
   * request fails, so the Select-location modal and the Created list always
   * have sections to work with. Its keys are verified against dev; the old
   * local seeds (`home-overview`, …) were not, and each produced `DSW024` on
   * save.
   */
  async fetchSections() {
    try {
      const { sections } = await listSections();
      if (sections.length) return sections;
      warnCatalogFallback('section/list');
    } catch (error) {
      warnCatalogFallback('section/list', error);
    }
    // TODO(TEMP-FALLBACK): remove this fallback (or reorganize it) once `section/list` returns real rows.
    return SECTION_FALLBACK_TREE;
  },

  /**
   * The Section filter's options, from `section/list_filter` — flat, and its
   * `name` is already the full location path. TEMP-FALLBACK: same fallback as
   * `fetchSections`, empty or failed.
   */
  async fetchSectionFilters() {
    let sections = [];
    try {
      ({ sections } = await listSectionFilters());
      if (!sections.length) warnCatalogFallback('section/list_filter');
    } catch (error) {
      warnCatalogFallback('section/list_filter', error);
    }
    // TODO(TEMP-FALLBACK): remove this fallback (or reorganize it) once `section/list_filter` returns real rows.
    const source = sections.length ? sections : SECTION_FALLBACK_FILTERS;
    return source.map((section) => ({
      id: section.sectionKey,
      name: section.name,
      path: section.name,
    }));
  },

  /**
   * The Categories filter's options, from `category/list_filter`.
   *
   * No fallback on purpose: dev has no dashboard categories at all (the
   * catalog answers `[]` and every dashboard's `categories` is `[]`, checked
   * 2026-10-01), so there are no real keys to offer. An empty answer leaves the
   * filter showing "No options available" rather than invented categories.
   */
  async fetchTemplateCategories() {
    const { categories } = await listDashboardCategories();
    return categories;
  },

  async getDashboardById(id) {
    const dashboard = await getDashboard(id);
    rememberRevision(id, dashboard.revision);
    return dashboard;
  },

  async getTemplateById(id) {
    return getTemplate(id);
  },

  /**
   * Create — in two calls, because the contract splits it in two.
   *
   * `create` refuses a populated composition: *"Initial composition on create
   * is not supported yet; create the dashboard, then update it with the
   * composition."* So the dashboard is created from its metadata and the
   * widgets follow in an `update`. The editor builds one payload and presses
   * save once; that this costs two round trips is the backend's shape, not the
   * screen's.
   *
   * **The dashboard exists after the first call, whatever the second does.**
   * If the composition write fails, retrying must not create a second
   * dashboard, so the id rides on the error and `useDashboardEditor` adopts it
   * — the retry is then an update of the draft that is already there.
   */
  async createDashboard(payload = {}) {
    const result = await createDashboard(payload);
    rememberRevision(result.id, result.revision);

    // Nothing to place: the dashboard was just created, so it is already
    // empty and a second call would say nothing.
    if (!Array.isArray(payload.widgets) || !payload.widgets.length) {
      return toUpsertedDashboard(result.id, payload, result);
    }

    let written;
    try {
      written = await updateDashboard(
        result.id,
        { widgets: payload.widgets, sections: payload.sections, filters: payload.filters },
        { expectedRevision: result.revision },
      );
    } catch (error) {
      error.dashboardId = result.id;
      throw error;
    }
    rememberRevision(result.id, written.revision);
    // `written`, not `result`: the `clientRef → instanceUuid` pairs come from
    // the call that actually wrote the placements.
    return toUpsertedDashboard(result.id, payload, written);
  },

  /**
   * Save — and, for the two state changes the list page expresses as a save,
   * the endpoint that actually performs them: archiving is `delete`, and
   * restoring is an update carrying `dashboardStatus: 'ACTIVE'`.
   */
  async updateDashboard(id, payload = {}) {
    if (payload.state === DASHBOARD_STATES.ARCHIVED) {
      const archived = await archiveDashboard(id);
      return { ...payload, id, state: archived.state };
    }

    const isRestoring = Boolean(payload.archivedFromState === null && payload.state);
    const result = await updateDashboard(
      id,
      isRestoring ? { ...payload, dashboardStatus: 'ACTIVE' } : payload,
      { expectedRevision: revisions.get(id) },
    );
    rememberRevision(id, result.revision);
    return toUpsertedDashboard(id, payload, result);
  },

  async publishDashboard(id) {
    const published = await publishDashboard(id);
    return {
      id,
      state: DASHBOARD_STATES.PUBLISHED,
      publishedVersionNumber: published.publishedVersionNumber,
      updatedAt: published.publishedAt ?? new Date().toISOString(),
    };
  },

  async duplicateDashboard(id) {
    const result = await duplicateDashboard(id);
    rememberRevision(result.id, result.revision);
    // The endpoint answers with ids only, so the copy is read back — it is the
    // only way the new card shows its widgets and its section.
    return apiDashboardDataAdapters.getDashboardById(result.id);
  },

  /**
   * The widgets a dashboard can be built from — the real catalog.
   *
   * Not filtered to `PUBLISHED`. The contract says an instance pins a published
   * version, but publishing is currently blocked upstream
   * (`DSW012 UI_WIDGET_QUERY_NOT_PUBLISHED`), so every widget in the workspace
   * is a draft and filtering would leave the modal permanently empty. Each card
   * carries its `status`, so the day publishing works this can narrow without
   * anything else changing.
   */
  async getAvailableWidgets() {
    const [widgets, { categories }] = await Promise.all([
      // Every page, for the same reason the two lists above take every page:
      // the modal renders this array as the whole catalogue and has no paging
      // control. The backend caps `pageSize` at 100 — asking for more comes
      // back as 100 — so a bigger catalogue has to be walked, not requested.
      fetchAllPages(
        (page) => listWidgets({ page, pageSize: 100, withGovernedCount: false }),
        (response) => response.widgets,
      ),
      listWidgetCategories(),
    ]);
    const categoryNames = new Map(categories.map((category) => [category.key, category.label]));
    return widgets.map((widget) => toPlaceableWidget(widget, categoryNames));
  },

  // --- Not in the phase-1 contract -----------------------------------------
  // TODO(CT-5183 / backend): no endpoint publishes share candidates or accepts
  // share assignments, so these two stay on the local seeds.
  getShareCandidates: defaultDashboardDataAdapters.getShareCandidates,
  shareDashboard: defaultDashboardDataAdapters.shareDashboard,
};

export default apiDashboardDataAdapters;
